import { useState } from "react";

import { useCoachOptimizationStore } from "@/client/stores/coach-optimization";

/**
 * Can this tab hand the optimized résumé back to the Career Coach tab?
 *
 * Both conditions are required, and both are things `Save and Continue` needs:
 *   - `window.opener` — the tab that opened this one, and the only `postMessage` recipient.
 *   - `session.returnOrigin` — the exact origin that message may be sent to.
 *
 * WHEN THIS IS FALSE. The student reloaded into the builder directly, or opened the URL themselves, or
 * closed the Career Coach tab. `window.opener` does survive a normal reload, so this is not simply
 * "was the page refreshed" — it is "is the receiving tab still reachable".
 *
 * WHY IT IS SHARED. Three places have to agree about it, and disagreeing would each be its own bug:
 *   - the confirm button's label — `Save and Continue` promises a handoff, so with no recipient it has
 *     to read `Save`, or it describes something that cannot happen.
 *   - the unload guard and its modal — warning "don't close, your score won't update" is pointless
 *     when the score already cannot be returned.
 *   - `CoachConfirmBar`'s error path, which tells the student to attach the résumé from the résumé
 *     picker instead.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */
const hasLiveOpener = () => {
  try {
    const opener = window.opener as Window | null;
    return Boolean(opener && !opener.closed);
  } catch {
    // Cross-origin `closed` access can throw in some browsers. An opener we cannot inspect is still
    // an opener, and postMessage to an explicit origin will work, so assume the handoff is live.
    return Boolean(window.opener);
  }
};

/**
 * Evaluated ONCE per mount.
 *
 * `window.opener` is not reactive — there is no event for the opener being closed — so the
 * alternative is polling, which would re-render on a timer to detect something `CoachConfirmBar`
 * already handles at the moment it matters (it logs and shows a toast if the message cannot be sent).
 * Freezing the value also keeps the button label, the guard and the modal consistent with each other
 * within a render rather than letting them read different answers.
 */
export const useCoachScoreReturnable = () => {
  const returnOrigin = useCoachOptimizationStore((state) => state.session?.returnOrigin);

  const [returnable] = useState(() => Boolean(returnOrigin) && hasLiveOpener());

  return returnable;
};
